import subscribe_products from "../../../assets/images/subscribe_products.gif";
import app_key_management from "../../../assets/images/app_key_management.gif";

const trimTrailingSlash = (url: string) => url.replace(/\/$/, "");

const docsApiBase = (mode: "live" | "test"): string => {
  const envKey =
    mode === "live"
      ? "REACT_APP_DOCS_LIVE_API_BASE"
      : "REACT_APP_DOCS_TEST_API_BASE";
  const fromEnv = process.env[envKey];
  if (fromEnv && fromEnv.trim()) {
    return trimTrailingSlash(fromEnv.trim());
  }
  return trimTrailingSlash(process.env.REACT_APP_SERVER_URI || "");
};

const curr = window.location.origin;
const live = docsApiBase("live");
const test = docsApiBase("test");

const w60 = "width:60px";
const w80 = "width:80px";
const w92 = "width:92px";
const w100 = "width:100px";
const w120 = "width:120px";
const w170 = "width:170px";
const w190 = "width:190px";
const w210 = "width:210px";
const tableHeight = "height:250px";

export const OpenApiDoc = {
  openapi: "3.0.0",
  servers: [
    {
      url: live,
      description: "Production URL",
    },
    {
      url: test,
      description: "Test URL",
    },
  ],
  info: {
    description: `
    \n# Introduction
    \n Welcome to ZOOP.ONE, one identity platform where everyone can establish and share their identities smoothly. We aim to bring a change in the conventional KYC process to make it highly secure and consent-driven by enabling users with a Universal Digital Identity.
    \n The Zoop API is organized around REST APIs. Our APIs have resource-oriented URLs which accept JSON-encoded request bodies and return JSON-encoded responses along with standard HTTP codes.
    \n To get started, you essentially need:
    \n- An active ZOOP.ONE account. You can quickly get started with a free Trial account by reaching out to us at [sales@zoop.one](mailto:sales@zoop.one).
    \n- Subscribe to the product you want to Test from the Products section.
    \n- Enable API Key and copy APP Id from the Manage App Section. This API key and App Id will be required for authentication.
    
    \n# Product Subscription
    \n- Go to the [Products](${curr}/application/product-catalog) section in the left navigation panel to subscribe to one or more products.
    \n- Products will be displayed on the right side along with their description.
    \n- Click on the Subscribe button to subscribe to a particular product.
    \n- You can start testing the subscribed products by using the trial credits allotted for that product. These credits will be deducted from your current plan with every API call.
    \n ![Product Subscription](${subscribe_products} "Product Subscription")
    
    \n# Products to App
    \n We provide the capability to create multiple apps to serve different use-cases of a business.
    \n- You can create a new App by navigating to the [Manage Apps](${curr}/application/manage-apps) section.
    \n- Click on the more info button to manage an App.
    \n- Now, click the Manage Products button in the Product Level Settings section to add or remove products.

    \n# App Key Management
    \n For your app security, you can add an API key to integrate into the subscribed products.
    \n You will receive the OTP in the admin email. Toggle on Enable to generate API key. Enter the OTP that you have received on the work email and click on Verify to complete app key management. 
    \n ![App Key Management](${app_key_management} "App Key Management")
    
    \n# API Base URL
    <table>
      <thead>
        <tr>
          <th style=${w120}>Environment</th>
          <th>Base URL</th>
          <th style=${w210}>APP ID and API Keys</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style=${w120}>test</td>
          <td>${test}/api/v1</td>
          <td style=${w210}>Use from test App</td>
        </tr>
        <tr>
          <td style=${w120}>live</td>
          <td>${live}/api/v1</td>
          <td style=${w210}>Use from production App</td>
        </tr>
      </tbody>
    </table>
    
    \n# Authentication Method
    \n The Zoop API uses API keys and APP ID to authenticate requests. You can view and manage your API Keys and App ID in the [Zoop customer dashboard](${curr}/application/dashboard).
    \n Do not share your secret API keys in publicly accessible areas such as GitHub, client-side code, and so forth.
    \n Your API keys carry many privileges, so be sure to keep them secure! Authentication to the API is performed via HTTPS Headers. Provide your API Key and APP ID in the headers to perform the request.
    <table>
      <thead>
        <tr>
          <th style=${w100}>Field</th>
          <th>Value</th>
          <th style=${w100}>Required</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style=${w100}>app-id</td>
          <td>ID of the created app. Available within the APP section. </td>
          <td style=${w100}>Y</td>
        </tr>
        <tr>
          <td style=${w100}>api-key</td>
          <td>API key generated and active within the APP with above app-id. </td>
          <td style=${w100}>Y</td>
        </tr>
      </tbody>
    </table>
    \n All API requests must be made over HTTPS. Calls made over plain HTTP will fail. API requests without authentication will also fail.
    \n **Note: Before moving to production it is advised to enable IP whitelisting from the App setting as by default all IPs are allowed.**
    
    \n# Error With Authentication
    <div>
      <pre>
      {
        "response_code": 401,
        "response_message": "App not found"
      }</pre>
      <pre>
      {
        "response_code": 403,
        "response_message": "ip not whitelisted"
      }</pre>
      <pre>
      {
        "response_code": 401,
        "response_message": "api key is Invalid or disabled"
      }</pre>
      <pre>
      {
        "response_code": 403,
        "response_message": "product not subscribed by app"
      }</pre>
      <pre>
      {
        "response_code": 403,
        "response_message": "action disallowed for the product"
      }</pre>
      <pre>
      {
        "response_code": 401,
        "response_message": "org not found"
      }</pre>
    </div>
    
    \n# Response Structure 
    \n Zoop API uses conventional HTTP response codes to indicate the success or failure of an API request.
    \n In general : Codes in the 2xx range indicate success. Codes in the 4xx range indicate an error that failed given the information provided (e.g., a required parameter was omitted, a charge failed, etc.). Codes in the 5xx range indicate an error with Zoop servers (these are rare). Some 4xx errors could be handled by the customer (e.g., app key is disabled).

    \n <h2>Following are the codes which you will receive while using API:</h2>
    <table style=${tableHeight}>
      <thead>
        <tr>
          <th style=${w120}>Status Code</th>
          <th style=${w120}>HTTP Code</th>
          <th>Response Message</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style=${w120}>100</td>
          <td style=${w120}>200</td>
          <td>Valid Authentication</td>
        </tr>
        <tr>
          <td style=${w120}>101</td>
          <td style=${w120}>200</td>
          <td>No Record Found</td>
        </tr>
        <tr>
          <td style=${w120}>102</td>
          <td style=${w120}>200</td>
          <td>Multiple Records Found</td>
        </tr>
        <tr>
          <td style=${w120}>103</td>
          <td style=${w120}>200</td>
          <td>Partial Record Found</td>
        </tr>
        <tr>
          <td style=${w120}>104</td>
          <td style=${w120}>400</td>
          <td>Missing or Invalid Consent</td>
        </tr>
        <tr>
          <td style=${w120}>105</td>
          <td style=${w120}>400</td>
          <td>Number of inputs exceeded</td>
        </tr>
        <tr>
          <td style=${w120}>106</td>
          <td style=${w120}>400</td>
          <td>Invalid ID Number or Combination of Inputs</td>
        </tr>
        <tr>
          <td style=${w120}>107</td>
          <td style=${w120}>400</td>
          <td>Duplicate Transaction</td>
        </tr>
        <tr>
          <td style=${w120}>108</td>
          <td style=${w120}>503</td>
          <td>Source Error</td>
        </tr>
        <tr>
          <td style=${w120}>109</td>
          <td style=${w120}>504</td>
          <td>Source timed out</td>
        </tr>
        <tr>
          <td style=${w120}>110</td>
          <td style=${w120}>503</td>
          <td>Source Down</td>
        </tr>
        <tr>
          <td style=${w120}>111</td>
          <td style=${w120}>500</td>
          <td>Internal Error</td>
        </tr>
        <tr>
          <td style=${w120}>112</td>
          <td style=${w120}>403</td>
          <td>You are not Authorized to make this request as your wallet balance is low</td>
        </tr>
        <tr>
          <td style=${w120}>113</td>
          <td style=${w120}>403</td>
          <td>Wallet balance reaching zero and there are too many pending requests</td>
        </tr>
        <tr>
          <td style=${w120}>99</td>
          <td style=${w120}>500</td>
          <td>Unknown Error</td>
        </tr>
      </tbody>
    </table>

    \n <h2>BAV response and reason codes:</h2>
    <table style=${tableHeight}>
      <thead>
        <tr>
          <th style=${w92}>Response Code</th>
          <th style=${w60}>HTTP Code</th>
          <th style=${w190} style=${w190}>Response Message</th>
          <th style=${w80}>Reason Code</th>
          <th>Response Message</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style=${w92}>100</td>
          <td style=${w60}>200</td>
          <td style=${w190}>Valid Authentication</td>
          <td style=${w80}>TXN</td>
          <td>Transaction Successful</td>
        </tr>
        <tr>
          <td style=${w92}>100</td>
          <td style=${w60}>200</td>
          <td style=${w190}>Valid Authentication</td>
          <td style=${w80}>NNR</td>
          <td>No Name Returned</td>
        </tr>
        <tr>
          <td style=${w92}>100</td>
          <td style=${w60}>200</td>
          <td style=${w190}>Valid Authentication</td>
          <td style=${w80}>TUP</td>
          <td>Transaction Under Process</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>NRE</td>
          <td>Non-residential External Account</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>IRA</td>
          <td>Invalid Refill Amount</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>IAN</td>
          <td>Invalid Account Number</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>ACCOUNT BLOCKED/FROZEN</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION NOT ALLOWED FROM NON-NRE TO NRE ACCOUNT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>IMPS not supported by beneficiary</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID BENEFICIARY IFSC CODE OR NBIN</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>NRE ACCOUNT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>PSP_API_NOT_REGISTERED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>NETWORK FAILURE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID ACCOUNT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>ACCOUNT CLOSED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Transaction rejected due to fraud check</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION NOT ALLOWED AS INVALID REMITTER ACC. NO</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>ACCOUNT BLOCKEDFROZEN</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BENEFICIARY IS NOT REGISTERED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Invalid IFSC</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID BENEFICIARY</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION NOT ALLOWED AS GENERAL ERROR</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID_TXN -Transaction ID are wrong in ARQC</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BENEFICIARY FORCED OFFLINE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BENEFICIARY BANK OFFLINE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Transaction type not supported. purpose = 00</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>DO NOT HONOUR (BENEFICIARY)</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION NOT ALLOWED FROM NONNRE TO NRE ACCOUNT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>UNABLE TO PROCESS</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>SUSPECTED FRAUD</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BENEFICIARY BANK DEEMED HIGH RESPONSE TIME CHECK DECLINE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BANK IS NOT REGISTERED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>PAYER BANK NOT REGISTERED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION NOT ALLOWED AS INVALID PAYMENT REFERENCE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BENEFICIARY ACCOUNT NOT ENABLED FOR IMPS CREDIT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Invalid account number format</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID BENEFICIARY MOBILE NO/MAS</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BENIFICARY BANK NOT ENABLE FOR P2A</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>EXCEEDS ACCOUNT LIMIT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>AMOUNT LIMIT EXCEEDED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>SUSPECTED FRAUD, DECLINE / TRANSACTIONS DECLINED BASED ON RISK SCORE BY BENEFICIARY</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>CUSTOMER TRANSACTION. LIMIT EXCEEDED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BENEFICIARY BANK DA RESPONSE TIME</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION AMOUNT LESS THAN RS.1</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Bank API request timed out.</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INSUFFICIENT BALANCE IN POOL A/C</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Something went wrong.</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION NOT ALLOWED FROM NONNRE TO NRE ACCOUNT AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>DUPLICATE TRANSACTION</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Service Provider Downtime</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Transaction Type Not Supported by Beneficiary Bank</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Invalid Beneficiary mobile number or account number</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION NOT ALLOWED AS GENERALERROR</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID ACCOUNT AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>CONNECTION TIMEOUT IN REQPAY CREDIT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>something went wrong</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>DEEMED APPROVED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID TRANSACTION OR IF MEMBER IS NOT ABLE TO FIND ANY APPROPRIATE RESPONSE CODE (BENEFICIARY)</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BENEFICIARY ACCOUNT BLOCKEDFROZEN AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>HIGH SEVERITY MEMO (HSM) PRESENT ON THE ACCOUNT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>IMPSERROR:Your request could not be processed</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>EXCEEDS DAILY AMOUNT LIMIT AT BENEFICIARY ACCOUNT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Validation failed: account.accountNumber: Account number contains invalid characters or patterns</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID RESPONSE CODE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Network connection error.</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>PAYER PSP NOT REGISTERED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>RISK THRESHOLD EXCEEDED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>PSP IS NOT REGISTERED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>ISSUER NODE OFFLINE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID BENEFICIARY IFSC CODE OR NBIN AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BREACHED MAXIMUM ALLOWED TRANSACTIONS FOR BENEFICIARY ACCOUNT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Payer PSP not available</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Beneficiary bank is down</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Too Many Requests</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>PAYER/PAYEE BANK NOT REGISTERED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>upstream connect error or disconnect/reset before headers. reset reason: connection termination</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID BENEFICIERY IFSC</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSFER AMOUNT EXCEEDS LIMIT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Unauthorized</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>IMPSERROR:IMPS Service not available for the selected bank</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Validation failed: account.ifsc: IFSC code contains invalid characters or patterns</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>DO NOT HONOUR</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Beneficiary account is not enabled for IMPS</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Payee.Ac.Detail.Value incorrect format for ACNUM purpose = 00</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BENEFICIARY ACCOUNT NOT ENABLED FOR INR CURRENCY</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>GRACEFUL DECLINE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>beneficiary bank deemed high response time check decline AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Error Beneficiary Not Found</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>The payee.accountNumber format is invalid.</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Unable to validate</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>UNABLE TO PROCESS DUE TO INTERNAL EXCEPTION AT SERVER/CBS/ETC ON BENEFICIARY SIDE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>IMPSERROR:Invalid IFS Code</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSFER NOT ALLOWED TO NON- KYC COMPLIANT ACCOUNT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Transaction is rejected and customer is not validated with reason:TRANSACTION FAILED the account does not exist.</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Transaction not allowed as invalid payment referance</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>CUSTOMER TRANSACTION LIMIT EXCEEDED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>IMPSERROR:ACCOUNT IS LOCKED - PLS. TRY AGAIN</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID BENEFICIARY MOBILE NOMAS</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSFER AMOUNT EXCEEDS MAXIMUM ALLOWED LIMIT FOR BENEFICIARY ACCOUNT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Transaction is rejected and customer is not validated with reason:The account does not exist.|@tranDtl.partTranDetailLL.&lt;rec_1&gt;.acctId.foracid|@BE|</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>NO ROUTING FOR INSTITUTION/NETWORK</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Account is not an NRE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>BENEFICIARY ACCOUNT IS CLOSED AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID TRANSACTION</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>NPCI Rejected</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>SSL certificate error.</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION NOT ALLOWED AS INVALID REMITTER ACCOUNT NUMBER AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>VERIFICATION SUCCESSFUL ORGINAL TRXN DECLINED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>CBSERROR:CREDIT NOT ALLOWED ON NRE ACCOUNT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INSUFFICENT BALANCE IN POOL A/C</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Beneficiary Account is Frozen</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>CBSERROR:INVALID CHECK DIGIT</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Internal Server Error</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>-RECORD NOT FOUND TD</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION TYPE NOT SUPPORTED</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>AMOUNT LIMIT EXCEEDED AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>AMT_LIMIT_EXCEEDED -Amount is passed more than the limit amount (Cumulative 2000 limit)</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Bad Request</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Beneficiary Bank Offline AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Beneficiary Bank account limit has exceeded</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>CBSERROR:Total freeze on account</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Duplicate Transaction Id</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Graceful decline</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>IMPSERROR:Invalid To Account Number</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>INVALID ACCOUNT ACCOUNT IS LOCKED PLS TRY AGAIN REVERSAL FAILURE</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Invalid Beneficiary Account Number</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>MULTIPLE ACCOUNTS LINKED TO SINGLE MOBILE NUMBER</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>PAYER.AC.DETAIL.VALUE INCORRECT FORMAT &lt;NAME&gt;</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION DECLINED DUE TO SUSPECTED FRAUD AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION NOT ALLOWED AS GENERAL ERROR AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>TRANSACTION TYPE NOT SUPPORTED AND REVERSAL IS SUCCESSFUL</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>The Beneficiary Bank is unable to process the transaction. Kindly check with the Beneficiary Bank and try again.</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Transaction is rejected and customer is not validated with reason:ACCOUNT FROZEN|0002408699842048 INR1.00|@M1009161/ 2|@BE|</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Transaction is rejected and customer is not validated with reason:The account has been closed.|@tranDtl.partTranDetailLL.&lt;rec_1&gt;.acctId.foracid|@BE|</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>URL Open error</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>Validation successful</td>
        </tr>
        <tr>
          <td style=${w92}>101</td>
          <td style=${w60}>200</td>
          <td style=${w190}>No Record Found</td>
          <td style=${w80}>ERR</td>
          <td>upstream connect error or disconnect/reset before headers. retried and the latest reset reason: connection termination</td>
        </tr>
        <tr>
          <td style=${w92}>107</td>
          <td style=${w60}>400</td>
          <td style=${w190}>Duplicate Transaction</td>
          <td style=${w80}>DLC</td>
          <td>Daily Transaction Limit exhausted for bank account number</td>
        </tr>
        <tr>
          <td style=${w92}>108</td>
          <td style=${w60}>503</td>
          <td style=${w190}>Source Down</td>
          <td style=${w80}>SPE</td>
          <td>Service Provider Error, Try Later</td>
        </tr>
        <tr>
          <td style=${w92}>110</td>
          <td style=${w60}>503</td>
          <td style=${w190}>Source Down</td>
          <td style=${w80}>SPD</td>
          <td>Service Provider Downtime</td>
        </tr>
        <tr>
          <td style=${w92}>111</td>
          <td style=${w60}>500</td>
          <td style=${w190}>Internal Error</td>
          <td style=${w80}>IE</td>
          <td>Internal Error</td>
        </tr>
      </tbody>
    </table>

    \n <h2>Zoop API response is divided into different parts such as:</h2>
    <table style=${tableHeight}>
      <thead>
        <tr>
          <th style=${w170}>Field</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style=${w170}>request_id</td>
          <td>Request ID is a unique ID which we assign to every task we receive from you. We recommend that you store this request ID.</td>
        </tr>
        <tr>
          <td style=${w170}>group_id</td>
          <td>Use this to group transactions for a single user at your end.</td>
        </tr>
        <tr>
          <td style=${w170}>task_id</td>
          <td>Use this to map your internal IDs for each API call. It is required that this should be unique in your system. (The Task ID should exclusively use the UUIDv4 format.)</td>
        </tr>
        <tr>
          <td style=${w170}>success</td>
          <td>If request was successful or not</td>
        </tr>
        <tr>
          <td style=${w170}>response_code</td>
          <td>Main codes explaining the response scenario. Recommended to use this for handling specific cases in your code.</td>
        </tr>
        <tr>
          <td style=${w170}>response_message</td>
          <td>Main message explaining the response scenario.</td>
        </tr>
        <tr>
          <td style=${w170}>metadata</td>
          <td>In the metadata section, you will get the billability of transactions, for each API billable code may differ.</td>
        </tr>
      </tbody>
    </table>
    `,
    title: "Zoop Products Documentation",
    contact: {
      url: curr + "/application/dashboard",
    },
    "x-logo": {
      url: "data:image/svg+xml,%3Csvg%20%20%20%20%20%20width%3D%22210%22%20%20%20%20%20%20height%3D%2250%22%20%20%20%20%20%20viewBox%3D%220%200%2084%2018%22%20%20%20%20%20%20fill%3D%22none%22%20%20%20%20%20%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20%20%20%20%3E%3Cg%20clip-path%3D%22url(%23clip0_593_29650)%22%3E%3Cpath%20%20%20%20%20%20%20%20%20%20d%3D%22M8.04325%200.0203305C6.20234%200.303553%204.87609%200.830479%203.57625%201.80529C1.78153%203.15553%200.56745%205.12492%200.118768%207.39729C-0.0395895%208.20084%20-0.0395895%209.80797%200.118768%2010.6115C0.851173%2014.3263%203.63564%2017.1322%207.33064%2017.8765C8.14884%2018.0412%209.94355%2018.0412%2010.7089%2017.8765C14.2852%2017.1059%2016.8651%2014.6754%2017.7955%2011.1977C17.9934%2010.4403%2018%2010.381%2018%209.0044C18%207.62781%2017.9934%207.56853%2017.7955%206.81108C16.8717%203.35971%2014.2984%200.916103%2010.7815%200.145475C10.2999%200.0400898%208.46555%20-0.0389486%208.04325%200.0203305ZM7.04692%203.51121C7.06672%203.57049%207.08651%203.69563%207.08651%203.80102C7.08651%204.14352%207.40982%205.25006%207.62756%205.66502C8.04986%206.46198%208.70309%206.92306%209.54106%207.01525L9.95675%207.06137V8.51041V9.95945L9.54765%209.99897C9.0198%2010.0517%208.35338%2010.3678%208.05645%2010.7235C7.9311%2010.8684%207.73315%2011.1648%207.62097%2011.3756C7.40982%2011.7773%207.08651%2012.8839%207.08651%2013.2198C7.08651%2013.6216%207.11951%2013.615%205.76686%2013.615H4.51979L4.5462%2013.3976C4.67815%2012.3833%204.74414%2012.0079%204.88271%2011.5205C5.19942%2010.4271%205.70088%209.5445%206.38711%208.87927L6.75659%208.51699L6.3409%208.08888C5.82624%207.55537%205.55573%207.16017%205.27199%206.54762C4.9223%205.79015%204.65176%204.71655%204.55938%203.71539L4.5264%203.40583H5.76686C6.83577%203.40583%207.01393%203.41899%207.04692%203.51121ZM11.3094%203.62977C11.3094%204.26866%2011.6723%205.40814%2012.0682%206.00093C12.4707%206.61348%2013.2229%207.02843%2013.9289%207.02843H14.2126L14.1994%208.49065L14.1796%209.95945L13.7705%209.99897C13.1767%2010.0582%2012.5631%2010.3744%2012.2199%2010.8025C11.7515%2011.3887%2011.4084%2012.324%2011.3226%2013.233L11.2896%2013.615H10.1745H9.05939L9.09898%2013.2988C9.3827%2011.2438%2010.2075%2010.1373%2011.5667%2010.0056L12.0022%209.95945V8.51041V7.06137L11.5931%207.01525C11.0257%206.95598%2010.445%206.67934%2010.1151%206.32367C9.64662%205.81651%209.22434%204.72973%209.10558%203.71539L9.06599%203.40583H10.1877H11.3094V3.62977Z%22%20%20%20%20%20%20%20%20%20%20fill%3D%22black%22%20%20%20%20%20%20%20%20%2F%3E%3Cpath%20%20%20%20%20%20%20%20%20%20d%3D%22M11.2683%203.5938C11.2683%204.22641%2011.6299%205.35467%2012.0244%205.94163C12.4255%206.54815%2013.175%206.95902%2013.8785%206.95902H14.1612L14.148%208.40685L14.1283%209.86119L13.7207%209.90032C13.1289%209.95901%2012.5175%2010.2721%2012.1756%2010.696C11.7088%2011.2764%2011.3669%2012.2025%2011.2815%2013.1025L11.2486%2013.4808H10.1375H9.02637L9.06582%2013.1677C9.34852%2011.1329%2010.1704%2010.0373%2011.5247%209.90685L11.9586%209.86119V8.42641V6.99164L11.551%206.94597C10.9856%206.88727%2010.407%206.61336%2010.0783%206.26119C9.6115%205.75903%209.19073%204.68294%209.07238%203.67858L9.03293%203.37207H10.1506H11.2683V3.5938Z%22%20%20%20%20%20%20%20%20%20%20fill%3D%22%2321FF90%22%20%20%20%20%20%20%20%20%2F%3E%3Cpath%20%20%20%20%20%20%20%20%20%20d%3D%22M7.02084%203.47641C7.04057%203.53511%207.06029%203.65901%207.06029%203.76337C7.06029%204.1025%207.38244%205.19815%207.5994%205.60902C8.0202%206.39814%208.67109%206.85468%209.50606%206.94597L9.92026%206.99164V8.42641V9.86119L9.51263%209.90032C8.98667%209.9525%208.32262%2010.2655%208.02676%2010.6177C7.90185%2010.7612%207.70461%2011.0547%207.59284%2011.2634C7.38244%2011.6612%207.06029%2012.7568%207.06029%2013.0894C7.06029%2013.4873%207.09317%2013.4808%205.74535%2013.4808H4.50275L4.52906%2013.2655C4.66054%2012.2612%204.7263%2011.8895%204.86437%2011.4068C5.17995%2010.3242%205.67961%209.45032%206.36338%208.79164L6.73155%208.43293L6.31735%208.00903C5.80453%207.48077%205.53498%207.08946%205.25226%206.48294C4.90381%205.73293%204.63425%204.66989%204.54219%203.67858L4.50933%203.37207H5.74535C6.81045%203.37207%206.98796%203.3851%207.02084%203.47641Z%22%20%20%20%20%20%20%20%20%20%20fill%3D%22%2321FF90%22%20%20%20%20%20%20%20%20%2F%3E%3Cpath%20%20%20%20%20%20%20%20%20%20d%3D%22M25.1095%201.58441C25.0843%201.60321%2025.0655%202.43693%2025.0655%203.43362V5.23896L27.8613%205.2515L30.6572%205.27031L27.3286%208.98753L24%2012.7048V14.5853V16.4596H29.8297H35.6595V14.6104V12.7612L32.2745%2012.7487L28.8895%2012.7298L32.2745%208.96873L35.6595%205.21389V3.37721V1.54053H30.4063C27.5166%201.54053%2025.1283%201.55933%2025.1095%201.58441Z%22%20%20%20%20%20%20%20%20%20%20fill%3D%22black%22%20%20%20%20%20%20%20%20%2F%3E%3Cpath%20%20%20%20%20%20%20%20%20%20d%3D%22M39.803%202.03574C39.1448%203.07006%2038.5368%204.45539%2038.2045%205.67775C37.8786%206.9064%2037.8284%207.35773%2037.8222%209.00008C37.8222%2010.329%2037.841%2010.6362%2037.9663%2011.2693C38.2986%2013.0182%2038.9882%2014.742%2039.9096%2016.1274L40.1289%2016.4596H44.6925H49.2496L49.6007%2015.908C50.5598%2014.4223%2051.2242%2012.5919%2051.4875%2010.7427C51.6067%209.90275%2051.6067%208.09741%2051.4875%207.25742C51.2242%205.40821%2050.5598%203.57781%2049.6007%202.08589L49.2496%201.54053H44.6862H40.1227L39.803%202.03574ZM46.3223%203.74706C47.0369%204.82524%2047.5071%205.91596%2047.7704%207.13833C47.9708%208.06606%2047.9708%209.9341%2047.7704%2010.8618C47.3879%2012.6233%2046.5668%2014.1654%2045.313%2015.4692L44.6987%2016.1149L44.2473%2015.6761C43.2694%2014.717%2042.3167%2013.2063%2041.903%2011.9463C41.5645%2010.9182%2041.4641%2010.2475%2041.4641%209.00008C41.4641%207.75265%2041.5645%207.08191%2041.903%206.05386C42.3167%204.78763%2043.282%203.25183%2044.2473%202.32409L44.7051%201.8853L45.3192%202.53096C45.6577%202.88826%2046.1091%203.43362%2046.3223%203.74706Z%22%20%20%20%20%20%20%20%20%20%20fill%3D%22black%22%20%20%20%20%20%20%20%20%2F%3E%3Cpath%20%20%20%20%20%20%20%20%20%20d%3D%22M58.0459%201.59446C58.0271%201.62581%2057.9957%201.8264%2057.9707%202.05207C57.7951%203.72577%2057.1495%205.26154%2056.3158%206.02005C55.7516%206.52152%2054.7862%206.87883%2053.9839%206.87883H53.7394V9.01014V11.1414H53.9839C54.4164%2011.1414%2055.1687%2011.3169%2055.5824%2011.5175C56.836%2012.1131%2057.6634%2013.6175%2057.9581%2015.8554L58.0333%2016.4383H61.669H65.2985L65.3801%2015.8178C65.4929%2014.89%2065.7436%2014.0061%2066.0883%2013.3041C66.7341%2011.9689%2067.6806%2011.2919%2069.066%2011.1665L69.5361%2011.1226V9.01014V6.89763L69.066%206.85376C67.6806%206.72838%2066.7341%206.05138%2066.0883%204.71618C65.7436%204.01411%2065.4929%203.13025%2065.3801%202.20251L65.2985%201.58193L61.6878%201.56312C59.7008%201.55685%2058.0583%201.56939%2058.0459%201.59446ZM61.832%203.0801C62.2082%205.12364%2063.1861%206.9854%2064.4146%208.01344C64.8283%208.36447%2065.4865%208.74686%2065.9443%208.90983C66.1511%208.98505%2066.1635%209.00386%2066.0571%209.04147C65.5618%209.22326%2064.8598%209.61818%2064.4898%209.93161C63.1672%2011.0411%2062.158%2013.0471%2061.7694%2015.3413L61.6566%2015.9995L61.6126%2015.6234C61.4685%2014.5076%2060.873%2012.7023%2060.315%2011.7307C59.5942%2010.4707%2058.4344%209.41758%2057.4566%209.12923C57.2059%209.06027%2057.093%208.94744%2057.2623%208.94744C57.2999%208.94744%2057.5694%208.83461%2057.8515%208.69671C59.7384%207.78149%2061.1048%205.58124%2061.5562%202.72279C61.619%202.33415%2061.6754%202.10221%2061.6878%202.20878C61.6942%202.30908%2061.763%202.70398%2061.832%203.0801Z%22%20%20%20%20%20%20%20%20%20%20fill%3D%22black%22%20%20%20%20%20%20%20%20%2F%3E%3Cpath%20%20%20%20%20%20%20%20%20%20d%3D%22M71.7199%201.63263C71.7011%201.67651%2071.6948%205.03018%2071.7011%209.07964L71.7199%2016.4452H73.5378H75.3555L75.3743%2014.5458L75.387%2012.6527H77.1359C79.0854%2012.6527%2079.6432%2012.59%2080.4519%2012.2766C81.1665%2012.0008%2081.6806%2011.6748%2082.2636%2011.1294C83.4169%2010.0325%2083.9937%208.7286%2083.9999%207.16773C83.9999%205.98298%2083.7178%204.99883%2083.091%204.06481C82.1884%202.72335%2080.7403%201.82069%2079.1481%201.62636C78.3456%201.52606%2071.7575%201.53233%2071.7199%201.63263ZM79.3173%205.43136C79.9693%205.77612%2080.339%206.38418%2080.339%207.1176C80.339%207.49997%2080.3078%207.61907%2080.1322%207.95757C79.9065%208.39638%2079.4928%208.75995%2079.0854%208.88532C78.935%208.9292%2078.2266%208.95426%2077.1171%208.95426H75.387V7.09879V5.24956L77.2237%205.26837C78.985%205.28717%2079.0666%205.29346%2079.3173%205.43136Z%22%20%20%20%20%20%20%20%20%20%20fill%3D%22black%22%20%20%20%20%20%20%20%20%2F%3E%3C%2Fg%3E%3Cdefs%3E%3CclipPath%20id%3D%22clip0_593_29650%22%3E%3Crect%20width%3D%2283.9999%22%20height%3D%2218%22%20fill%3D%22white%22%20%2F%3E%3C%2FclipPath%3E%3C%2Fdefs%3E%3C%2Fsvg%3E",
      altText: "Zoop logo",
    },
  },
  "x-tagGroups": [],
  paths: {},
  components: {},
};

export const OpenApiTheme = {
  codeBlock: {
    backgroundColor: "#838383",
  },
  colors: {
    primary: {
      main: "#0056b3",
    },
  },
  logo: {
    gutter: "0.8em",
    maxHeight: "50px",
    maxWidth: "300px",
    display: "flex",
  },
  rightPanel: {
    backgroundColor: "#3E464F",
  },
  schema: {
    defaultDetailsWidth: "60%",
  },
};
